import React, { useEffect, useState } from 'react';
import './ARVSection.scss';
import { Button, Message, SelectPicker, toaster, Modal } from 'rsuite';
import {
  getActiveLocations,
  getAccuracyARVData,
  importRowsToFirestore,
  useGetMethods,
  useGetInstruments,
  useGetPersonnel
} from '../../util/db';
import { uniq, sortBy } from 'lodash';
import FunnelIcon from '@rsuite/icons/Funnel';
import AddOutlineIcon from '@rsuite/icons/AddOutline';
import ArchiveIcon from '@rsuite/icons/Archive';
import SearchDataLiveIcon from '@rsuite/icons/SearchDataLive';
import RefreshIcon from '@rsuite/icons/Reload';

import ARVDataTable from './ARVDataTable';
import ARVLineChart from './ARVChart';
import CSVImportButton, { parseCSVFile } from './CSVImportButton';
import { calculateARVRows } from './ARVCalculation';
import { useAuth } from '../../util/auth';
import { checkHasPermission, isAdmin } from '../../util/util';
import { RULES, SCREEN } from '../../util/Constant';

const ARVSection = () => {
  const auth = useAuth();

  const { data: personnel } = useGetPersonnel();

  const [permissionData, setPermissionData] = useState([]);
  const [allARVData, setAllARVData] = useState([]);
  const [dataARV, setARVData] = useState([]);

  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState([]);

  const [rounds, setRounds] = useState([]);
  const [methods, setMethods] = useState([]);
  const [instruments, setInstruments] = useState([]);

  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [reIndex, setReIndex] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedParameter, setSelectedParameter] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');

  const { data: rawMethods } = useGetMethods('');
  const { data: rawInstruments } = useGetInstruments('');

  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingValidRows, setPendingValidRows] = useState([]);
  const [pendingInvalidRows, setPendingInvalidRows] = useState([]);

  const parameterOptions = [
    { label: 'RVP', value: 'RVP' },
    { label: 'Sulfur', value: 'Sulfur' },
    { label: 'Benzene', value: 'Benzene' },
    { label: 'Oxygenate', value: 'Oxygenate' }
  ];
  const materialOptions = [
    { label: 'Gasoline', value: 'Gasoline' },
    { label: 'ULSD', value: 'ULSD' },
    { label: '500 ppm diesel fuel', value: '500 ppm diesel fuel' },
    { label: 'ECA marine Fuel', value: 'ECA marine Fuel' },
    { label: 'Butane', value: 'Butane' }
  ];

  const filterFields = [
    {
      label: 'Location',
      value: selectedLocation,
      onChange: (v) => setSelectedLocation(v?.trim() || ''),
      data: locationFilter
    },
    {
      label: 'Round',
      value: selectedRound,
      onChange: (v) => setSelectedRound(v?.trim() || ''),
      data: rounds
    },
    {
      label: 'Method',
      value: selectedMethod,
      onChange: (v) => setSelectedMethod(v?.trim() || ''),
      data: methods
    },
    {
      label: 'Instrument',
      value: selectedInstrument,
      onChange: (v) => setSelectedInstrument(v?.trim() || ''),
      data: instruments
    },
    {
      label: 'Parameter',
      value: selectedParameter,
      onChange: (v) => setSelectedParameter(v?.trim() || ''),
      data: parameterOptions
    }
  ];

  const trimRowFields = (row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.trim() : value
      ])
    );

  const resetFilters = () => {
    setSelectedLocation('');
    setSelectedRound('');
    setSelectedMethod('');
    setSelectedParameter('');
    setSelectedInstrument('');
    setARVData(allARVData);
  };

  const refreshData = async () => {
    setTableLoading(true);
    try {
      const newData = await getAccuracyARVData();
      setAllARVData(newData);
      setARVData(applyFiltersTo(newData));
    } catch (err) {
      console.error('Failed to refresh ARV data:', err);
    } finally {
      setTableLoading(false);
    }
  };

  const applyFiltersTo = (dataset) => {
    if (!dataset || !Array.isArray(dataset)) return [];
    return dataset.filter(
      (d) =>
        (!selectedLocation || d.location?.trim() === selectedLocation.trim()) &&
        (!selectedRound || d.round?.trim() === selectedRound.trim()) &&
        (!selectedMethod || d.method?.trim() === selectedMethod.trim()) &&
        (!selectedInstrument || d.instrument?.trim() === selectedInstrument.trim()) &&
        (!selectedParameter || d.parameter?.trim() === selectedParameter.trim())
    );
  };

  const handleFilter = () => {
    setLoadingFilter(true);
    const filtered = applyFiltersTo(allARVData);
    setARVData(filtered);
    setLoadingFilter(false);
  };

  const handleEvaluateData = async () => {
    setLoading(true);
    setTableLoading(true);
    try {
      const rawData = await getAccuracyARVData();

      const { invalidRows } = calculateARVRows(rawData, materialOptions);

      if (invalidRows.length > 0) {
        toaster.push(
          <Message type="warning" closable showIcon>
            Found {invalidRows.length} invalid rows during evaluation.
          </Message>,
          { placement: 'topEnd' }
        );
      }

      const newData = await getAccuracyARVData();
      resetFilters();
      setAllARVData(newData);
      setARVData(newData);

      toaster.push(
        <Message type="success" closable showIcon>
          Data evaluated and updated successfully!
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (err) {
      console.error('Evaluation failed:', err);
      toaster.push(
        <Message type="error" closable showIcon>
          Evaluation failed: {err.message || String(err)}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handleAddRow = () => {
    const newRow = trimRowFields({
      key: Date.now().toString(),
      isNew: true,
      location: '',
      instrument: '',
      method: '',
      parameter: '',
      material: 'Gasoline',
      size: '250 mL',
      round: '',
      labs: '',
      arv: '',
      measuredValue: '',
      delta: '',
      r: '',
      deltaA: '',
      passFail: ''
    });

    setARVData((prev) => [newRow, ...prev]);
  };

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setTableLoading(true);
    try {
      const { validRows, invalidRows: parseInvalid } = await parseCSVFile(file);
      const trimmedValidRows = validRows.map(trimRowFields);

      const { validRows: calculatedValid, invalidRows: calculatedInvalid } = calculateARVRows(
        trimmedValidRows,
        materialOptions
      );

      const allInvalid = [...parseInvalid, ...calculatedInvalid];

      if (allInvalid.length > 0) {
        setPendingValidRows(calculatedValid);
        setPendingInvalidRows(allInvalid);
        setShowImportModal(true);
        return;
      }

      if (calculatedValid.length > 0) {
        await importRowsToFirestore(calculatedValid);
        setAllARVData((prev) => [...prev, ...calculatedValid]);
        setARVData((prev) => [...prev, ...calculatedValid]);
        await refreshData();
        toaster.push(
          <Message type="success" closable showIcon>
            Imported {calculatedValid.length} row(s) successfully!
          </Message>,
          { placement: 'topEnd' }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" closable showIcon>
          Import failed: {err.message || String(err)}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setLoading(false);
      setTableLoading(false);
      e.target.value = '';
    }
  };

  function buildFilters({ data, rawMethods, rawInstruments, masterLocations = [] }) {
    const uniqMethods = uniq([
      ...(rawMethods || []).map((m) => m.method?.trim()).filter(Boolean),
      ...data.map((d) => d.method?.trim()).filter(Boolean)
    ]);
    const sortedMethods = sortBy(uniqMethods, (m) => m.toLowerCase());

    const uniqLocations = uniq(data.map((d) => d.location?.trim()).filter(Boolean));
    const allLocations = uniq([
      ...uniqLocations,
      ...masterLocations.map((l) => l?.trim()).filter(Boolean)
    ]);
    const sortedLocations = sortBy(allLocations, (l) => l.toLowerCase());
    const sortedUniqLocations = sortBy(uniqLocations, (l) => l.toLowerCase());

    const uniqRounds = uniq(data.map((d) => d.round?.trim()).filter(Boolean));
    const sortedRounds = sortBy(uniqRounds, (r) => r.toLowerCase());

    const uniqInstruments = uniq([
      ...(rawInstruments || []).map((i) => i.name?.trim()).filter(Boolean),
      ...data.map((d) => d.instrument?.trim()).filter(Boolean)
    ]);
    const sortedInstruments = sortBy(uniqInstruments, (i) => i.toLowerCase());

    return {
      locations: sortedLocations.map((l) => ({ label: l, value: l })),
      locationFilter: sortedUniqLocations.map((l) => ({ label: l, value: l })),
      rounds: sortedRounds.map((r) => ({ label: r, value: r })),
      methods: sortedMethods.map((m) => ({ label: m, value: m })),
      instruments: sortedInstruments.map((i) => ({ label: i, value: i }))
    };
  }

  useEffect(() => {
    fetchAndReindexFilters();
  }, []);
  useEffect(() => {
    if (!personnel || !auth?.user?.email || !auth?.permissionData) return;

    const matchingUser = personnel.find((person) => person.email === auth.user.email);

    if (!matchingUser) return;

    const userRole = matchingUser.permission;
    const rolePermissionData = auth.permissionData[userRole];

    setPermissionData(rolePermissionData || null);
  }, [personnel, auth]);

  const fetchAndReindexFilters = async () => {
    try {
      const [masterLocations, accuracyARVData] = await Promise.all([
        getActiveLocations(),
        getAccuracyARVData()
      ]);

      const normalizedData = accuracyARVData.map(trimRowFields);

      setAllARVData(normalizedData);
      setARVData((prev) => (prev.length === 0 ? normalizedData : prev));

      const filters = buildFilters({
        data: normalizedData,
        rawMethods,
        rawInstruments,
        masterLocations
      });

      setLocations(filters.locations);
      setLocationFilter(filters.locationFilter);

      setRounds(filters.rounds);
      setSelectedRound((prev) =>
        prev && filters.rounds.find((r) => r.value === prev) ? prev : ''
      );

      setMethods(filters.methods);
      setSelectedMethod((prev) =>
        prev && filters.methods.find((m) => m.value === prev) ? prev : ''
      );

      setInstruments(filters.instruments);
      setSelectedInstrument((prev) =>
        prev && filters.instruments.find((i) => i.value === prev) ? prev : ''
      );
    } catch (err) {
      console.error(err);
      toaster.push(
        <Message type="error" closable showIcon>
          Failed to fetch filters
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  return (
    <div>
      <div className="ARVSection">
        <label className="title">1090 accuracy with ARV</label>

        <div className="tableSection">
          <div className="filterRow">
            {filterFields.map(({ label, value, onChange, data }) => (
              <div className="filterComponent" key={label}>
                <label>{label}</label>
                <SelectPicker
                  loading={loadingFilter}
                  className="select"
                  data={data}
                  value={value}
                  onClean={() => onChange('')}
                  onChange={onChange}
                />
              </div>
            ))}

            <Button
              appearance="primary"
              onClick={handleFilter}
              startIcon={<FunnelIcon />}
              loading={loadingFilter}
              style={{ minWidth: '120px', alignSelf: 'end' }}
            >
              Apply filter
            </Button>

            {(isAdmin(permissionData) ||
              checkHasPermission(permissionData?.[SCREEN?.ARV_DATA], RULES.CREATE) ||
              checkHasPermission(permissionData?.[SCREEN?.ARV_DATA], RULES.UPDATE)) && (
              <Button
                appearance="ghost"
                onClick={() => {
                  try {
                    setReIndex(true);
                    fetchAndReindexFilters();
                    setTimeout(() => setReIndex(false), 200);
                    toaster.push(
                      <Message type="success" closable showIcon>
                        Filters re-indexed
                      </Message>,
                      { placement: 'topEnd' }
                    );
                  } catch (err) {
                    console.error(err);
                    toaster.push(
                      <Message type="error" closable showIcon>
                        Failed to re-index filters
                      </Message>,
                      { placement: 'topEnd' }
                    );
                  }
                }}
                loading={reIndex}
                startIcon={<RefreshIcon />}
                style={{ minWidth: '120px', alignSelf: 'end', marginLeft: 8 }}
              >
                Re-index
              </Button>
            )}
          </div>

          <ARVDataTable
            rows={dataARV}
            setARVData={setARVData}
            setAllARVData={setAllARVData}
            locations={locations}
            methods={methods}
            instruments={instruments}
            parameters={parameterOptions}
            resetFilters={resetFilters}
            tableLoading={tableLoading}
            setTableLoading={setTableLoading}
            refreshData={refreshData}
            materialOptions={materialOptions}
          />
          {(isAdmin(permissionData) ||
            checkHasPermission(permissionData?.[SCREEN?.ARV_DATA], RULES.CREATE)) && (
            <div className="buttonRow ">
              <div className="buttonGroup ">
                <Button
                  appearance="primary"
                  onClick={handleAddRow}
                  loading={loading || tableLoading}
                  style={{ minWidth: '120px', alignSelf: 'end' }}
                  startIcon={<AddOutlineIcon />}
                >
                  Add Row
                </Button>

                <CSVImportButton
                  loading={loading || tableLoading}
                  setARVData={setARVData}
                  title={'Add Data From File'}
                  startIcon={<ArchiveIcon />}
                  handleFileImport={handleFileImport}
                />
              </div>

              <Button
                className="buttonRight"
                appearance="primary"
                onClick={handleEvaluateData}
                loading={loading || tableLoading}
                style={{ minWidth: '120px' }}
                startIcon={<SearchDataLiveIcon />}
              >
                Evaluate Data
              </Button>
            </div>
          )}
        </div>

        <div className="tableSection">
          <ARVLineChart
            data={allARVData}
            locations={locationFilter}
            methods={methods}
            instruments={instruments}
            parameters={parameterOptions}
          />
        </div>
      </div>

      {/* Modal for invalid rows */}
      <Modal open={showImportModal} onClose={() => setShowImportModal(false)} size="md">
        <Modal.Header>
          <Modal.Title>Invalid Rows Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {pendingInvalidRows.map((r, i) => (
                <div key={i}>
                  Row {r.rowNumber}:{' '}
                  {r.missing?.length > 0 ? (
                    <strong>missing {r.missing.join(', ')}</strong>
                  ) : (
                    <strong>{r.reason}</strong>
                  )}
                </div>
              ))}
            </div>
          </Message>

          <p style={{ marginTop: 12 }}>
            Do you want to skip that and importing the other {pendingValidRows.length} row(s)?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={async () => {
              await importRowsToFirestore(pendingValidRows.map(trimRowFields));
              await refreshData();
              toaster.push(
                <Message type="success" closable showIcon>
                  Imported {pendingValidRows.length} row(s) successfully
                </Message>,
                { placement: 'topEnd' }
              );
              setShowImportModal(false);
              setPendingValidRows([]);
              setPendingInvalidRows([]);
            }}
          >
            Skip
          </Button>

          <Button
            appearance="subtle"
            onClick={() => {
              setShowImportModal(false);
              setPendingValidRows([]);
              setPendingInvalidRows([]);
              toaster.push(
                <Message type="info" closable showIcon>
                  Import cancelled
                </Message>,
                { placement: 'topEnd' }
              );
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ARVSection;
